<template>
    <div :class="containerClassName">
        <v-card-title>
            <v-layout
                :class="{'mb-4': type === 'normal'}"
                row
                wrap
                align-center
                justify-space-between
            >
                <h3 v-if="type === 'normal' || type === 'no-frame'" class="title font-weight-bold mb-3 d-flex align-center">
                    {{ title }}
                    <v-tooltip v-if="!loading" bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                type="button"
                                class="mb-2"
                                color="tertiary"
                                :disabled="!!error"
                                @click="exportData"
                                flat
                                icon
                            >
                                <v-icon small>fa-download</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('graph.export') }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="!loading" bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                type="button"
                                class="mb-2"
                                color="tertiary"
                                :disabled="!!error"
                                @click="expand"
                                flat
                                icon
                            >
                                <v-icon small>fa-expand</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('graph.fullScreen') }}</span>
                    </v-tooltip>
                    <slot name="leftFilters" />
                    <InfoTooltip v-if="tooltip" :text="tooltip" class="ml-1" />
                </h3>
                <h4 v-if="type === 'small'" class="subheading font-weight-bold">
                    {{ title }}
                    <v-tooltip v-if="!loading" bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                type="button"
                                class="mb-2"
                                color="tertiary"
                                :disabled="!!error"
                                @click="exportData"
                                flat
                                icon
                            >
                                <v-icon small>fa-download</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('graph.export') }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="!loading" bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                type="button"
                                class="mb-2"
                                color="tertiary"
                                :disabled="!!error"
                                @click="expand"
                                flat
                                icon
                            >
                                <v-icon small>fa-expand</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('graph.fullScreen') }}</span>
                    </v-tooltip>
                    <slot name="leftFilters" />
                    <InfoTooltip v-if="tooltip" :text="tooltip" class="ml-1" />
                </h4>
                <v-layout
                    wrap
                    align-center
                    justify-end
                >
                    <slot name="filters" />
                    <v-btn
                        v-if="error"
                        class="mb-3"
                        @click="redo()"
                        flat
                        icon
                    >
                        <v-icon small>fa-redo-alt</v-icon>
                    </v-btn>
                </v-layout>
                <slot name="secondaryFilters" />
            </v-layout>
        </v-card-title>
        <v-card-text v-if="$slots.afterFilters">
            <slot name="afterFilters" />
        </v-card-text>
        <v-card-text>
            <graph-container v-model="expanded" :title="title">
                <column-chart-graph
                    v-if="!loading && chartData"
                    ref="chart"
                    :data="chartData"
                    :options="chartOptions"
                    :events="chartEvents"
                    :expanded="expanded"
                    :style-text-node="styleTextNode"
                />
            </graph-container>
            <data-placeholder v-if="!loading && !chartData" :title="noResultsTitle" />
            <div v-if="loading" class="mt-2 text-xs-center">
                <v-progress-circular indeterminate color="primary" />
            </div>
            <v-alert
                :value="error"
                dismissible
                type="error"
                transition="scale-transition">
                {{ error }}
            </v-alert>
            <slot name="footer" />
        </v-card-text>
    </div>
</template>

<script>
import { merge } from 'lodash';
import InfoTooltip from '@/components/common/InfoTooltip.vue';
import ColumnChartGraph from './core/ColumnChartGraph.vue';
import DataPlaceholder from '../common/DataPlaceholder.vue';
import expandGraphMixin from '../../mixins/expandGraphMixin';
import GraphContainer from './GraphContainer.vue';

export default {
    name: 'column-graph',
    components: {
        GraphContainer,
        ColumnChartGraph,
        DataPlaceholder,
        InfoTooltip,
    },
    mixins: [
        expandGraphMixin,
    ],
    props: {
        type: {
            validator: value => ['small', 'normal', 'no-frame'].includes(value),
            default: 'normal',
        },
        permissionName: {
            type: String,
        },
        title: {
            type: String,
            required: true,
        },
        data: {
            type: Array,
            default: null,
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        events: {
            type: Object,
            default: () => ({}),
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
        noResultsTitle: {
            type: String,
            default() {
                return this.$t('noResults');
            },
        },
        tooltip: {
            type: String,
            default: null,
        },
        styleTextNode: {
            type: Function,
        },
    },
    computed: {
        containerClassName() {
            if (this.type === 'normal') {
                return 'grey lighten-4 mb-4 pa-4';
            }
            if (this.type === 'small' || this.type === 'no-frame') {
                return '';
            }
            return '';
        },
        chartData() {
            if (!this.data || this.data.length === 0) {
                return null;
            }

            return this.data;
        },
        chartOptions() {
            const baseOptions = {
                vAxes: {
                    0: {
                        format: 'short',
                        viewWindow: {
                            min: 0,
                        },
                    },
                },
            };

            let hasValueOverZero = false;

            if (this.data && this.data.length > 0) {
                // first row is column configuration
                // first row item is date
                hasValueOverZero = this.data.slice(1).some(row => row.slice(1).some(item => {
                    if (typeof item === 'object') {
                        return item.v > 0;
                    }
                    return item > 0;
                }));
            }

            if (!hasValueOverZero) {
                baseOptions.vAxes[0].viewWindow.max = 1;
            } else {
                baseOptions.vAxes[0].viewWindow.max = null;
            }

            return merge(baseOptions, this.options);
        },
        chartEvents() {
            return this.events;
        },
    },
    methods: {
        redo() {
            this.$emit('redo');
        },
        exportData() {
            this.$refs.chart?.exportData(this.title);
        },
    },
};
</script>

<style lang="scss" scoped>
</style>
